'use client';

/**
 * "Hielp dit artikel?" onder een kennisbankartikel.
 *
 * Een kennisbank zonder deze vraag is blind: je weet niet welk artikel zijn
 * werk doet en welk artikel klanten verder het bos in stuurt. De tellers
 * voeden de admin, waar de artikelen met veel "nee" bovenaan de herschrijflijst
 * horen.
 */

import { useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';

const COPY: Record<string, { vraag: string; ja: string; nee: string; dank: string; dankNee: string }> = {
  nl: { vraag: 'Hielp dit artikel?', ja: 'Ja', nee: 'Nee', dank: 'Fijn om te horen, bedankt!', dankNee: 'Bedankt. Stel je vraag gerust in de chat, dan kijken we mee.' },
  de: { vraag: 'Hat dieser Artikel geholfen?', ja: 'Ja', nee: 'Nein', dank: 'Schön zu hören, danke!', dankNee: 'Danke. Stellen Sie Ihre Frage gern im Chat, wir schauen mit.' },
  en: { vraag: 'Did this article help?', ja: 'Yes', nee: 'No', dank: 'Great to hear, thanks!', dankNee: 'Thanks. Feel free to ask in the chat and we will take a look.' },
  fr: { vraag: 'Cet article vous a-t-il aidé ?', ja: 'Oui', nee: 'Non', dank: 'Ravi de l’entendre, merci !', dankNee: 'Merci. Posez votre question dans le chat, nous regardons avec vous.' },
};

export function HielpDit({ artikelId, locale }: { artikelId: string; locale: string }) {
  const t = COPY[locale] || COPY.en;
  const [gestemd, setGestemd] = useState<null | boolean>(null);

  const stem = (nuttig: boolean) => {
    setGestemd(nuttig);
    // Vuur en vergeet: de klant hoeft nooit op deze teller te wachten.
    fetch('/api/kennisbank/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: artikelId, nuttig }),
    }).catch(() => undefined);
  };

  if (gestemd !== null) {
    return (
      <p className="mt-8 rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700">
        {gestemd ? t.dank : t.dankNee}
      </p>
    );
  }

  return (
    <div className="mt-8 flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-sm font-semibold text-gray-900">{t.vraag}</p>
      <button
        type="button"
        onClick={() => stem(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:border-green-400 hover:bg-green-50 hover:text-green-800"
      >
        <ThumbsUp className="h-4 w-4" />
        {t.ja}
      </button>
      <button
        type="button"
        onClick={() => stem(false)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:border-red-400 hover:bg-red-50 hover:text-red-800"
      >
        <ThumbsDown className="h-4 w-4" />
        {t.nee}
      </button>
    </div>
  );
}
